import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../../../data/getters";
import authApi from "../../../data/api/authApi";
import { useLanguage } from "../../helpers/LanguageContext";

const EMPTY_FORM = {
  name: "",
  userName: "",
  email: "",
  address: "",
  role: "user"
};

export default function useUsersPageController({ onCreated, enabled = true } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createForm, setCreateForm] = useState(EMPTY_FORM);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const allUsers = await dispatch(getAllUsers());
      setUsers(allUsers.filter((user) => user.role === "user"));
    } catch (error) {
      console.error("Failed to load users", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (enabled) loadUsers();
  }, [loadUsers, enabled]);

  const openCreate = useCallback(() => {
    setCreateErr("");
    setShowCreate(true);
  }, []);

  const closeCreate = useCallback(() => {
    setShowCreate(false);
  }, []);

  const handleCreateChange = useCallback((event) => {
    const { name, value } = event.target;
    setCreateForm((form) => ({ ...form, [name]: value }));
  }, []);

  const handleCreateUser = useCallback(
    async (event) => {
      event.preventDefault();
      setCreating(true);
      setCreateErr("");

      try {
        const response = await authApi.registerApi({
          ...createForm,
          password: "Welcome1"
        });
        const { success, message } = response.data;

        if (!success) {
          setCreateErr(message || t("createUserError"));
          return;
        }

        onCreated?.(response.data.user);
        setShowCreate(false);
        setCreateForm(EMPTY_FORM);
        await loadUsers();
      } catch (error) {
        const message =
          error.response?.data?.message ||
          JSON.stringify(error.response?.data) ||
          t("createUserError");
        setCreateErr(message);
      } finally {
        setCreating(false);
      }
    },
    [createForm, loadUsers, onCreated, t]
  );

  const handleUserClick = useCallback(
    (userId) => {
      navigate(`/userAnalyzer/${userId}`);
    },
    [navigate]
  );

  return {
    t,
    users,
    loading,
    showCreate,
    creating,
    createErr,
    createForm,
    openCreate,
    closeCreate,
    handleCreateChange,
    handleCreateUser,
    handleUserClick
  };
}
